import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Trophy, Calendar, Clock, Users, TrendingUp, Award, 
  Waves, Timer, Target, Zap, Medal, Star, ChevronRight,
  Filter, Search, MapPin, Info
} from 'lucide-react';
import { BRAND_GREEN, BRAND_GOLD } from '../theme/sucronTheme';
import { useSportsLang } from '../contexts/SportsLangContext';

const LABELS = {
  pt: {
    loading: 'Carregando hub de natação...',
    hubTitle: 'Hub de Natação',
    viewStats: 'Ver Stats',
    viewCommunity: 'Ver Comunidade',
    liveNow: 'AO VIVO',
    eventsCompleted: (n) => `${n} eventos concluídos`,
    eventsTotal: (n) => `${n} total`,
    viewLiveResults: 'Ver Resultados Ao Vivo',
    searchPlaceholder: 'Buscar atleta, competição ou país...',
    filterAll: 'Todos os Estilos',
    filterMale: 'Masculino',
    filterFemale: 'Feminino',
    filterBrazil: 'Brasil',
    todayTitle: 'Hoje na Piscina',
    male: 'Masculino',
    female: 'Feminino',
    mixed: 'Misto',
    live: 'AO VIVO',
    completed: 'Concluído',
    worldRecordBadge: 'RECORDE MUNDIAL!',
    worldRecordTitle: (name) => `${name} quebra Recorde Mundial!`,
    newTime: 'Novo Tempo',
    oldRecord: 'Recorde Anterior',
    improvement: 'Melhoria',
    viewEventAnalysis: 'Ver análise detalhada da prova',
    featuredAthletes: 'Atletas em Destaque',
    latestResults: 'Últimos Resultados',
    viewAll: 'Ver todos',
    viewFullAnalysis: 'Ver análise completa',
    worldRankings: 'Rankings Mundiais',
    newsTitle: 'Notícias e Análises',
    dateLocale: 'pt-BR',
    footerTitle: 'Hub de Dados Profissional',
    footerText: 'Acesso a splits, tempos de reação, análises biomânicas e comparações detalhadas de atletas. Dados oficiais da World Aquatics, CBDA e principais competições mundiais.',
  },
  en: {
    loading: 'Loading swimming hub...',
    hubTitle: 'Swimming Hub',
    viewStats: 'View Stats',
    viewCommunity: 'View Community',
    liveNow: 'LIVE',
    eventsCompleted: (n) => `${n} events completed`,
    eventsTotal: (n) => `${n} total`,
    viewLiveResults: 'View Live Results',
    searchPlaceholder: 'Search athlete, competition or country...',
    filterAll: 'All Strokes',
    filterMale: 'Male',
    filterFemale: 'Female',
    filterBrazil: 'Brazil',
    todayTitle: 'Today in the Pool',
    male: 'Male',
    female: 'Female',
    mixed: 'Mixed',
    live: 'LIVE',
    completed: 'Completed',
    worldRecordBadge: 'WORLD RECORD!',
    worldRecordTitle: (name) => `${name} breaks World Record!`,
    newTime: 'New Time',
    oldRecord: 'Previous Record',
    improvement: 'Improvement',
    viewEventAnalysis: 'View detailed race analysis',
    featuredAthletes: 'Featured Athletes',
    latestResults: 'Latest Results',
    viewAll: 'View all',
    viewFullAnalysis: 'View full analysis',
    worldRankings: 'World Rankings',
    newsTitle: 'News & Analysis',
    dateLocale: 'en-US',
    footerTitle: 'Professional Data Hub',
    footerText: 'Access splits, reaction times, biomechanical analysis and detailed athlete comparisons. Official data from World Aquatics, CBDA and major world competitions.',
  }
};

// === CORES & TEMA ===
const BORDER = '#E5E7EB';

// UI Primitives (padrão do site)
const PageShell = ({ children }) => (
  <div className="min-h-screen" style={{ backgroundColor: '#f9fafb' }}>
    <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
      {children}
    </div>
  </div>
);

// ============================================
// COMPONENTES DE UTILIDADE
// ============================================

const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-gray-100 ${className}`}>
    {children}
  </div>
);

const Badge = ({ children, variant = 'default' }) => {
  const variants = {
    default: 'bg-gray-100 text-gray-700',
    gold: 'bg-yellow-100 text-yellow-800',
    silver: 'bg-gray-200 text-gray-700',
    bronze: 'bg-orange-100 text-orange-700',
    record: 'bg-red-100 text-red-700',
    live: 'bg-emerald-50 text-emerald-800 animate-pulse'
  };
  
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${variants[variant]}`}>
      {children}
    </span>
  );
};

// ============================================
// DADOS SIMULADOS (Em produção, viriam da API)
// ============================================

const featuredCompetition = {
  id: 1,
  name: 'Campeonato Mundial FINA 2025',
  location: 'Fukuoka, Japão',
  dates: '15-24 de Julho, 2025',
  status: 'live',
  day: '3 de 10',
  image: '/competitions/worlds-2025.jpg',
  totalEvents: 42,
  completedEvents: 18
};

const todayEvents = [
  {
    id: 1,
    time: '09:00',
    session: 'Eliminatórias',
    events: [
      { distance: '100m', stroke: 'Livre', gender: 'M', status: 'completed' },
      { distance: '200m', stroke: 'Costas', gender: 'F', status: 'completed' },
      { distance: '50m', stroke: 'Peito', gender: 'M', status: 'live' }
    ]
  },
  {
    id: 2,
    time: '19:00',
    session: 'Finais',
    events: [
      { distance: '100m', stroke: 'Livre', gender: 'M', status: 'upcoming' },
      { distance: '200m', stroke: 'Costas', gender: 'F', status: 'upcoming' },
      { distance: '4x100m', stroke: 'Medley', gender: 'Misto', status: 'upcoming' }
    ]
  }
];

const worldRecord = {
  athlete: {
    name: 'Caeleb Dressel',
    country: 'USA',
    photo: '/athletes/dressel.jpg'
  },
  event: '50m Borboleta Masculino',
  newTime: '21.23',
  oldTime: '21.28',
  competition: 'Mundial FINA 2025',
  improvement: '0.05s'
};

const featuredAthletes = [
  {
    id: 1,
    name: 'Katie Ledecky',
    country: 'USA',
    photo: '/athletes/ledecky.jpg',
    achievement: '5x Campeã Olímpica',
    medals: { gold: 8, silver: 1, bronze: 0 },
    specialty: '800m Livre'
  },
  {
    id: 2,
    name: 'Adam Peaty',
    country: 'GBR',
    photo: '/athletes/peaty.jpg',
    achievement: 'Recordista Mundial',
    medals: { gold: 3, silver: 2, bronze: 1 },
    specialty: '100m Peito'
  },
  {
    id: 3,
    name: 'Fernando Scheffer',
    country: 'BRA',
    photo: '/athletes/scheffer.jpg',
    achievement: 'Bronze Olímpico',
    medals: { gold: 0, silver: 1, bronze: 2 },
    specialty: '200m Livre'
  }
];

const latestResults = [
  {
    id: 1,
    event: '100m Livre Masculino - Final A',
    competition: 'Mundial FINA 2025',
    date: '2025-07-20',
    podium: [
      { rank: 1, name: 'Kyle Chalmers', country: 'AUS', time: '46.82', medal: 'gold' },
      { rank: 2, name: 'David Popovici', country: 'ROU', time: '46.95', medal: 'silver' },
      { rank: 3, name: 'Hwang Sunwoo', country: 'KOR', time: '47.12', medal: 'bronze' }
    ]
  },
  {
    id: 2,
    event: '200m Costas Feminino - Final A',
    competition: 'Mundial FINA 2025',
    date: '2025-07-20',
    podium: [
      { rank: 1, name: 'Kaylee McKeown', country: 'AUS', time: '2:03.14', medal: 'gold' },
      { rank: 2, name: 'Regan Smith', country: 'USA', time: '2:03.89', medal: 'silver' },
      { rank: 3, name: 'Kylie Masse', country: 'CAN', time: '2:04.21', medal: 'bronze' }
    ]
  }
];

const topRankings = [
  {
    event: '100m Livre Masculino',
    rankings: [
      { rank: 1, name: 'David Popovici', country: 'ROU', time: '46.86', points: 988 },
      { rank: 2, name: 'Kyle Chalmers', country: 'AUS', time: '46.96', points: 978 },
      { rank: 3, name: 'Pan Zhanle', country: 'CHN', time: '47.02', points: 972 },
      { rank: 4, name: 'Hwang Sunwoo', country: 'KOR', time: '47.15', points: 959 },
      { rank: 5, name: 'Matt Richards', country: 'GBR', time: '47.24', points: 950 }
    ]
  }
];

const recentNews = [
  {
    id: 1,
    title: 'Análise: A estratégia de prova de Dressel nos 50m Borboleta',
    excerpt: 'Tempo de reação de 0.59s e splits perfeitos garantiram o novo recorde mundial...',
    author: 'SwimTech Analysis',
    date: '2025-07-20',
    readTime: '5 min'
  },
  {
    id: 2,
    title: 'Brasil conquista primeira medalha no Mundial com Scheffer',
    excerpt: 'Prata nos 200m livre marca o melhor resultado brasileiro na história...',
    author: 'VUKZ',
    date: '2025-07-19',
    readTime: '3 min'
  }
];

// ============================================
// COMPONENTES DE CARD
// ============================================

const HeroCard = ({ competition }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  return (
  <Card className="relative overflow-hidden h-64 mb-6">
    <div 
      className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900"
      style={{ 
        backgroundImage: competition.image ? `url(${competition.image})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
    </div>
    
    <div className="relative h-full flex flex-col justify-end p-6 text-white">
      <div className="flex items-center gap-2 mb-3">
        <Badge variant="live">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            {t.liveNow}
          </div>
        </Badge>
        <Badge variant="default">
          <span className="text-white">{competition.day}</span>
        </Badge>
      </div>
      
      <h1 className="text-3xl font-black mb-2">{competition.name}</h1>
      
      <div className="flex items-center gap-4 text-sm mb-4">
        <div className="flex items-center gap-1">
          <MapPin className="w-4 h-4" />
          {competition.location}
        </div>
        <div className="flex items-center gap-1">
          <Calendar className="w-4 h-4" />
          {competition.dates}
        </div>
      </div>
      
      {/* Barra de Progresso */}
      <div className="mb-4">
        <div className="flex justify-between text-xs mb-1">
          <span>{t.eventsCompleted(competition.completedEvents)}</span>
          <span>{t.eventsTotal(competition.totalEvents)}</span>
        </div>
        <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden">
          <div 
            className="h-full transition-all duration-500"
            style={{ width: `${(competition.completedEvents / competition.totalEvents) * 100}%`, backgroundColor: BRAND_GREEN }}
          />
        </div>
      </div>
      
      <Link
        to="/natacao/mundial-2025"
        className="w-full h-12 flex items-center justify-center rounded-xl font-bold text-white hover:opacity-90 transition-all"
        style={{ backgroundColor: BRAND_GREEN }}
      >
        {t.viewLiveResults}
        <ChevronRight className="w-5 h-5 ml-1" />
      </Link>
    </div>
  </Card>
  );
};

const SearchBar = ({ onSearch }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  
  return (
    <div className="sticky top-0 z-40 bg-gray-50 py-4 mb-6 -mx-4 px-4 shadow-sm">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder={t.searchPlaceholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full h-12 pl-11 pr-4 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:border-transparent"
          style={{ '--tw-ring-color': BRAND_GREEN }}
        />
      </div>
      
      <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
        <button className="px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap bg-white border border-gray-200 hover:border-gray-300">
          <Filter className="w-4 h-4 inline mr-1" />
          {t.filterAll}
        </button>
        <button className="px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap bg-white border border-gray-200">
          {t.filterMale}
        </button>
        <button className="px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap bg-white border border-gray-200">
          {t.filterFemale}
        </button>
        <button className="px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap bg-white border border-gray-200">
          {t.filterBrazil}
        </button>
      </div>
    </div>
  );
};

const TodayScheduleCard = ({ events }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  return (
  <Card className="mb-6 overflow-hidden">
    <div className="p-5 border-b border-gray-100">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5" style={{ color: BRAND_GREEN }} />
          <h2 className="text-xl font-black">{t.todayTitle}</h2>
        </div>
        <span className="text-sm text-gray-500">20 de Julho</span>
      </div>
    </div>
    
    {events.map((session) => (
      <div key={session.id} className="border-b border-gray-100 last:border-0">
        <div className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center justify-center w-16 h-16 rounded-xl bg-blue-50">
              <div className="text-center">
                <div className="text-xl font-bold text-blue-600">{session.time}</div>
                <div className="text-xs text-gray-500">{session.session}</div>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900">{session.session}</h3>
              <p className="text-sm text-gray-500">{session.events.length} eventos</p>
            </div>
          </div>
          
          <div className="space-y-2">
            {session.events.map((event, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-3">
                  <Waves className="w-5 h-5 text-blue-500" />
                  <div>
                    <div className="font-semibold text-sm">
                      {event.distance} {event.stroke} {event.gender === 'M' ? t.male : event.gender === 'F' ? t.female : t.mixed}
                    </div>
                  </div>
                </div>
                {event.status === 'live' && (
                  <Badge variant="live">{t.live}</Badge>
                )}
                {event.status === 'completed' && (
                  <span className="text-xs text-gray-500">{t.completed}</span>
                )}
                {event.status === 'upcoming' && (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    ))}
  </Card>
  );
};

const WorldRecordAlert = ({ record }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  return (
  <Card className="mb-6 overflow-hidden bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200">
    <div className="p-6">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
            <Zap className="w-8 h-8 text-white" />
          </div>
        </div>
        
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="record">{t.worldRecordBadge}</Badge>
            <span className="text-xs text-gray-500">Há 2 horas</span>
          </div>
          
          <h3 className="text-xl font-black text-gray-900 mb-1">
            {t.worldRecordTitle(record.athlete.name)}
          </h3>
          
          <p className="text-sm text-gray-600 mb-3">
            {record.event} • {record.competition}
          </p>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="text-center">
              <div className="text-3xl font-black" style={{ color: BRAND_GREEN }}>
                {record.newTime}
              </div>
              <div className="text-xs text-gray-500">{t.newTime}</div>
            </div>
            
            <div className="text-2xl text-gray-400">→</div>
            
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-400 line-through">
                {record.oldTime}
              </div>
              <div className="text-xs text-gray-500">{t.oldRecord}</div>
            </div>
            
            <div className="ml-auto text-center">
              <div className="text-xl font-bold text-red-600">
                -{record.improvement}
              </div>
              <div className="text-xs text-gray-500">{t.improvement}</div>
            </div>
          </div>
          
          <Link
            to={`/natacao/atleta/${record.athlete.name}`}
            className="inline-flex items-center gap-1 text-sm font-semibold hover:underline"
            style={{ color: BRAND_GREEN }}
          >
            {t.viewEventAnalysis}
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  </Card>
  );
};

const FeaturedAthletesCard = ({ athletes }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  return (
  <Card className="mb-6">
    <div className="p-5 border-b border-gray-100">
      <div className="flex items-center gap-2">
        <Star className="w-5 h-5" style={{ color: BRAND_GREEN }} />
        <h2 className="text-xl font-black">{t.featuredAthletes}</h2>
      </div>
    </div>
    
    <div className="divide-y divide-gray-100">
      {athletes.map((athlete) => (
        <Link
          key={athlete.id}
          to={`/natacao/atleta/${athlete.id}`}
          className="block p-5 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xl font-black flex-shrink-0">
              {athlete.name.split(' ').map(n => n[0]).join('')}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-bold text-gray-900 truncate">{athlete.name}</h3>
                <span className="text-xl">{athlete.country === 'USA' ? '🇺🇸' : athlete.country === 'GBR' ? '🇬🇧' : '🇧🇷'}</span>
              </div>
              <p className="text-sm text-gray-600 mb-2">{athlete.achievement}</p>
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 rounded-full bg-yellow-400" />
                  {athlete.medals.gold}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 rounded-full bg-gray-400" />
                  {athlete.medals.silver}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 rounded-full bg-orange-600" />
                  {athlete.medals.bronze}
                </span>
                <span className="ml-auto text-gray-500">{athlete.specialty}</span>
              </div>
            </div>
            
            <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
          </div>
        </Link>
      ))}
    </div>
  </Card>
  );
};

const LatestResultsCard = ({ results }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  return (
  <Card className="mb-6">
    <div className="p-5 border-b border-gray-100">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5" style={{ color: BRAND_GREEN }} />
          <h2 className="text-xl font-black">{t.latestResults}</h2>
        </div>
        <Link to="/natacao/resultados" className="text-sm font-semibold" style={{ color: BRAND_GREEN }}>
          {t.viewAll}
        </Link>
      </div>
    </div>
    
    <div className="divide-y divide-gray-100">
      {results.map((result) => (
        <div key={result.id} className="p-5">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="font-bold text-gray-900 mb-1">{result.event}</h3>
              <p className="text-xs text-gray-500">{result.competition} • {new Date(result.date).toLocaleDateString(t.dateLocale)}</p>
            </div>
          </div>
          
          <div className="space-y-2">
            {result.podium.map((athlete) => (
              <div key={athlete.rank} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  athlete.medal === 'gold' ? 'bg-yellow-400' :
                  athlete.medal === 'silver' ? 'bg-gray-400' :
                  'bg-orange-600'
                }`}>
                  <span className="text-white font-bold text-sm">{athlete.rank}</span>
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm truncate">{athlete.name}</span>
                    <span className="text-lg">{athlete.country === 'AUS' ? '🇦🇺' : athlete.country === 'ROU' ? '🇷🇴' : athlete.country === 'KOR' ? '🇰🇷' : athlete.country === 'USA' ? '🇺🇸' : '🇨🇦'}</span>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="font-bold text-gray-900">{athlete.time}</div>
                </div>
              </div>
            ))}
          </div>
          
          <Link
            to={`/natacao/prova/${result.id}`}
            className="mt-3 flex items-center justify-center w-full h-10 rounded-lg border-2 font-semibold text-sm hover:bg-gray-50 transition-colors"
            style={{ borderColor: BRAND_GREEN, color: BRAND_GREEN }}
          >
            {t.viewFullAnalysis}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Link>
        </div>
      ))}
    </div>
  </Card>
  );
};

const RankingsCard = ({ rankings }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  return (
  <Card className="mb-6">
    <div className="p-5 border-b border-gray-100">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5" style={{ color: BRAND_GREEN }} />
          <h2 className="text-xl font-black">{t.worldRankings}</h2>
        </div>
        <Link to="/natacao/rankings" className="text-sm font-semibold" style={{ color: BRAND_GREEN }}>
          {t.viewAll}
        </Link>
      </div>
    </div>
    
    {rankings.map((ranking, idx) => (
      <div key={idx} className="p-5 border-b border-gray-100 last:border-0">
        <h3 className="font-bold text-gray-900 mb-4">{ranking.event}</h3>
        
        <div className="space-y-2">
          {ranking.rankings.map((athlete) => (
            <div key={athlete.rank} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
              <div className="w-8 text-center font-bold text-gray-400 flex-shrink-0">
                {athlete.rank}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm truncate">{athlete.name}</span>
                  <span>{athlete.country === 'ROU' ? '🇷🇴' : athlete.country === 'AUS' ? '🇦🇺' : athlete.country === 'CHN' ? '🇨🇳' : athlete.country === 'KOR' ? '🇰🇷' : '🇬🇧'}</span>
                </div>
              </div>
              
              <div className="text-right flex items-center gap-3">
                <div className="font-bold text-gray-900">{athlete.time}</div>
                <div className="text-xs text-gray-500 w-12 text-right">{athlete.points}pts</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </Card>
  );
};

const NewsCard = ({ news }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  return (
  <Card className="mb-6">
    <div className="p-5 border-b border-gray-100">
      <div className="flex items-center gap-2">
        <Award className="w-5 h-5" style={{ color: BRAND_GREEN }} />
        <h2 className="text-xl font-black">{t.newsTitle}</h2>
      </div>
    </div>
    
    <div className="divide-y divide-gray-100">
      {news.map((article) => (
        <Link
          key={article.id}
          to={`/natacao/noticia/${article.id}`}
          className="block p-5 hover:bg-gray-50 transition-colors"
        >
          <h3 className="font-bold text-gray-900 mb-2 line-clamp-2">{article.title}</h3>
          <p className="text-sm text-gray-600 mb-3 line-clamp-2">{article.excerpt}</p>
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>{article.author}</span>
            <div className="flex items-center gap-2">
              <span>{new Date(article.date).toLocaleDateString(t.dateLocale)}</span>
              <span>•</span>
              <span>{article.readTime}</span>
            </div>
          </div>
        </Link>
      ))}
    </div>
  </Card>
  );
};

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

const SwimmingHubPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  
  useEffect(() => {
    // Simular carregamento de dados
    setTimeout(() => setIsLoading(false), 500);
  }, []);
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Waves className="w-12 h-12 mx-auto mb-4 animate-bounce" style={{ color: BRAND_GREEN }} />
          <p className="text-gray-600 font-semibold">{t.loading}</p>
        </div>
      </div>
    );
  }
  
  return (
    <PageShell>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/vukz-logo.jpeg" alt="VUKZ" className="h-10" />
            </Link>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Waves className="w-6 h-6" style={{ color: BRAND_GREEN }} />
                <h1 className="text-xl font-black" style={{ color: BRAND_GREEN }}>{t.hubTitle}</h1>
              </div>
              <Link
                to="/natacao/stats"
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm transition-all hover:shadow-md"
                style={{ backgroundColor: BRAND_GREEN, color: 'white' }}
              >
                <TrendingUp size={16} />
                {t.viewStats}
              </Link>
              <Link
                to="/natacao/comunidade"
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm transition-all hover:shadow-md"
                style={{ backgroundColor: BRAND_GOLD, color: 'white' }}
              >
                <Users size={16} />
                {t.viewCommunity}
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Hero - Competição em Destaque */}
        <HeroCard competition={featuredCompetition} />
        
        {/* Barra de Pesquisa Sticky */}
        <SearchBar />
        
        {/* Hoje na Piscina */}
        <TodayScheduleCard events={todayEvents} />
        
        {/* Alerta de Recorde Mundial */}
        <WorldRecordAlert record={worldRecord} />
        
        {/* Atletas em Destaque */}
        <FeaturedAthletesCard athletes={featuredAthletes} />
        
        {/* Últimos Resultados */}
        <LatestResultsCard results={latestResults} />
        
        {/* Rankings em Foco */}
        <RankingsCard rankings={topRankings} />
        
        {/* Notícias e Análises */}
        <NewsCard news={recentNews} />
        
        {/* Footer Info */}
        <div className="mt-8 p-6 bg-blue-50 rounded-xl border border-blue-100">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-blue-900 mb-1">{t.footerTitle}</h3>
              <p className="text-sm text-blue-700 leading-relaxed">
                {t.footerText}
              </p>
            </div>
          </div>
        </div>
      </main>
    </PageShell>
  );
};

export default SwimmingHubPage;
